/**
 * §3 auth — minimal JWT-bearing client for the operator session.
 *
 * Tokens live in localStorage (session-persistent across tab reopens
 * on the same machine). The refresh token trades for new access
 * tokens via /api/admin/refresh. A 401 from the API triggers a
 * single silent-refresh attempt before bouncing the user to /login.
 */

const ACCESS_KEY = "xpand.access_token";
const REFRESH_KEY = "xpand.refresh_token";
const TENANT_KEY = "xpand.tenant_id";
const OPERATOR_KEY = "xpand.operator";

export interface OperatorSummary {
  id: number;
  username: string;
  display_name?: string;
  // §v3.tenant-binding (2026-05-01) — backend now includes the
  // operator's bound tenant in the login response so we can store
  // it in localStorage and send the right X-Tenant-ID on every
  // request. Optional in the type for backward compat with old
  // backends that don't set it (treat absence as "fall back to
  // existing TENANT_KEY value").
  tenant_id?: string;
  // §rbac (2026-05-08) — login response now includes the operator's
  // role binding. The frontend reads ``role_name`` for surface
  // labels and refetches /api/admin/me for the canonical permission
  // list (so a role's permissions can change without forcing logout).
  email?: string | null;
  role_id?: number | null;
  role_name?: string | null;
}

export interface LoginResponse {
  success?: boolean;
  access_token?: string;
  refresh_token?: string;
  session_token?: string;
  access_expires_in?: number;
  operator?: OperatorSummary;
  permissions?: string[];
  branding?: unknown;
  stats?: unknown;
  // §v3.mfa — when an account has 2FA on, /api/admin/login returns
  // success=false + mfa_required=true + a one-time mfa_challenge instead
  // of tokens; the client finishes via /api/admin/login/mfa.
  mfa_required?: boolean;
  mfa_challenge?: string;
  // §security.mandatory-mfa (2026-06-20) — when the tenant REQUIRES 2FA and the
  // operator isn't enrolled, login returns mfa_enrollment_required + the QR data;
  // the client enrolls + finishes via /api/admin/login/mfa-enroll (which returns
  // one-time backup_codes alongside the normal success payload).
  mfa_enrollment_required?: boolean;
  otpauth_uri?: string;
  secret?: string;
  backup_codes?: string[];
  message?: string;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function getTenantId(): string {
  // §v4.tenant-isolation — NEUTRAL fallback (empty string), not the legacy
  // 'roze' default. persistLogin() writes the operator's bound tenant at login
  // and clearSession() clears it at logout, so an authenticated session always
  // has a real value here. The only time this fallback fires is pre-login or
  // after a backend regression that drops tenant_id — and sending
  // X-Tenant-ID: 'roze' there would silently mis-route another tenant's operator
  // into roze's data. Empty string makes that failure loud (the backend's
  // tenant-binding check rejects it) instead of silently wrong.
  return localStorage.getItem(TENANT_KEY) ?? "";
}

export function setTenantId(tenant: string): void {
  localStorage.setItem(TENANT_KEY, tenant);
}

export function getOperator(): OperatorSummary | null {
  const raw = localStorage.getItem(OPERATOR_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OperatorSummary;
  } catch {
    return null;
  }
}

/**
 * §v4.tenant-isolation (2026-06-16) — true when a master (platform) operator
 * has switched into a customer tenant: the audited impersonation session. The
 * login identity stays 'master' (operator.tenant_id / role_name); only the
 * active tenant header changes — so this is reload-safe (both reads come from
 * localStorage, which main.tsx re-applies on boot).
 */
export function isImpersonating(): boolean {
  const op = getOperator();
  const t = getTenantId();
  return (
    (op?.tenant_id === "master" || op?.role_name === "Master Admin") &&
    t !== "master"
  );
}

export function persistLogin(response: LoginResponse): void {
  if (response.access_token) localStorage.setItem(ACCESS_KEY, response.access_token);
  if (response.refresh_token) localStorage.setItem(REFRESH_KEY, response.refresh_token);
  if (response.operator) {
    localStorage.setItem(OPERATOR_KEY, JSON.stringify(response.operator));
    // §v3.tenant-binding (2026-05-01) — write the operator's bound
    // tenant from the login response into TENANT_KEY so the very
    // next request sends the right X-Tenant-ID. Without this, a
    // fresh login inherits whatever tenant_id was cached from a
    // previous operator's session — which fails the new backend
    // tenant-binding check (X-Tenant-ID 'roze' from a stale cache
    // doesn't match the new operator's tenant 'dcsn').
    if (response.operator.tenant_id) {
      localStorage.setItem(TENANT_KEY, response.operator.tenant_id);
    }
  }
}

// §qa-wave2 (2026-08-10) — per-tenant UI caches that must not survive a
// sign-out. The Overview's pinned 24h verdict snapshot
// (`xpand-overview-verdict-…`) carries one tenant's real numbers, and the
// legacy unkeyed variant of that key leaked them to whoever signed in next
// on the same browser for up to a day. The Launchpad dismissal flag
// (`xpand_launchpad_dismissed…`) is the same shape of bug in miniature: a
// fresh tenant inherited the previous tenant's "hide my checklist".
// Prefix-matched so both the legacy unkeyed keys and the current
// tenant-scoped keys are swept.
const TENANT_SCOPED_UI_KEY_PREFIXES = [
  "xpand-overview-verdict-",
  "xpand_launchpad_dismissed",
] as const;

// §stale-session (2026-08-10) — same-tab session-death signal. The browser's
// "storage" event only fires in OTHER tabs, so when api.ts cleared a dead
// session (401 + failed refresh) the tab that discovered it never re-checked
// isAuthenticated() and kept the dead shell mounted. clearSession dispatches
// this; App.tsx's RequireAuth listens for both events.
export const SESSION_CLEARED_EVENT = "xpand:session-cleared";

export function clearSession(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(OPERATOR_KEY);
  // §v3.tenant-binding (2026-05-01) — also clear the cached
  // tenant_id. The previous "keep it across logout" optimisation
  // was made when this was a single-tenant deploy; in a multi-
  // tenant world it leaks the previous operator's tenant into the
  // next sign-in, which then fails the backend's tenant-binding
  // enforcement on every request until persistLogin overwrites it.
  // Safer to start fresh and let persistLogin set it correctly.
  localStorage.removeItem(TENANT_KEY);
  // §qa-wave2 — purge tenant-scoped UI caches (collect first: removing
  // while iterating shifts localStorage's index-based cursor).
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (TENANT_SCOPED_UI_KEY_PREFIXES.some((p) => key.startsWith(p))) {
        doomed.push(key);
      }
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    /* storage unavailable — non-fatal, the token removals above matter most */
  }
  // §stale-session — tell THIS tab the session died (see const above).
  try {
    window.dispatchEvent(new Event(SESSION_CLEARED_EVENT));
  } catch {
    /* non-browser env — the next route change re-checks isAuthenticated() */
  }
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken());
}

// §security.session-expired (2026-06-20) — when a 401 + silent-refresh both fail
// (access token expired AND the refresh token is expired/revoked), the API layer
// flags it here BEFORE clearing the session. The login page consumes the flag on
// mount to show a friendly "your session expired, please sign in again" banner
// instead of a blank form. Set ONLY on token-expiry (never on an explicit logout),
// so a deliberate sign-out shows no scary message.
const SESSION_EXPIRED_KEY = "xpand.session_expired";

export function markSessionExpired(): void {
  try {
    localStorage.setItem(SESSION_EXPIRED_KEY, "1");
  } catch {
    /* storage unavailable — non-fatal, the redirect still happens */
  }
}

export function consumeSessionExpired(): boolean {
  try {
    const flag = localStorage.getItem(SESSION_EXPIRED_KEY) === "1";
    if (flag) localStorage.removeItem(SESSION_EXPIRED_KEY);
    return flag;
  } catch {
    return false;
  }
}
